import { SYSTEM_PAGES, SystemPage, findSystemPage } from './system-pages.config';

export type SystemUpdateBodySegment =
  | { kind: 'text'; text: string }
  | { kind: 'page'; page: SystemPage };

/** `[[page_key]]` — keys are lowercase letters, digits, `-`, `_` and `.`. */
const MARKER = /\[\[\s*([\w.-]+)\s*\]\]/g;

/**
 * Splits an update body into text and page-link segments.
 *
 * A marker whose key is in the catalogue becomes a `page` segment (an inline chip that navigates to
 * the page); an unknown marker is swallowed — it must never reach a reader as raw `[[…]]` — and
 * the single space left on both sides of it collapses. Adjacent text is merged, so the result never
 * holds two text segments in a row.
 */
export function parseSystemUpdateBody(
  body: string | null | undefined,
  pages: readonly SystemPage[] = SYSTEM_PAGES,
): SystemUpdateBodySegment[] {
  const text = body ?? '';
  const segments: SystemUpdateBodySegment[] = [];
  let cursor = 0;
  let swallowed = false;

  const pushText = (chunk: string): void => {
    if (!chunk) {
      return;
    }
    let value = chunk;
    const last = segments[segments.length - 1];
    if (swallowed && last?.kind === 'text' && last.text.endsWith(' ') && value.startsWith(' ')) {
      value = value.slice(1);
    }
    swallowed = false;
    if (!value) {
      return;
    }
    if (last?.kind === 'text') {
      last.text += value;
    } else {
      segments.push({ kind: 'text', text: value });
    }
  };

  for (const match of text.matchAll(MARKER)) {
    pushText(text.slice(cursor, match.index));
    cursor = (match.index ?? 0) + match[0].length;
    const page = findSystemPage(match[1], pages);
    if (page) {
      swallowed = false;
      segments.push({ kind: 'page', page });
    } else {
      swallowed = true;
    }
  }
  pushText(text.slice(cursor));
  return segments;
}

/** Keys of the catalogue pages a body links to, in order of first use. */
export function linkedPageKeys(body: string | null | undefined, pages: readonly SystemPage[] = SYSTEM_PAGES): string[] {
  const keys: string[] = [];
  for (const segment of parseSystemUpdateBody(body, pages)) {
    if (segment.kind === 'page' && !keys.includes(segment.page.key)) {
      keys.push(segment.page.key);
    }
  }
  return keys;
}

/**
 * Inserts `[[key]]` into `body` over the `[start, end)` selection, padding with a space where it
 * would otherwise touch a word. Returns the new body and the caret position right after the marker.
 */
export function insertPageMarker(
  body: string,
  start: number,
  end: number,
  key: string,
): { body: string; caret: number } {
  const from = clamp(Math.min(start, end), body.length);
  const to = clamp(Math.max(start, end), body.length);
  const before = body.slice(0, from);
  const after = body.slice(to);
  const lead = before && !/\s$/.test(before) ? ' ' : '';
  const trail = after && !/^[\s.,;:!?)]/.test(after) ? ' ' : '';
  const marker = `${lead}[[${key}]]${trail}`;
  return { body: before + marker + after, caret: before.length + marker.length };
}

function clamp(value: number, max: number): number {
  return Math.max(0, Math.min(Number.isFinite(value) ? value : max, max));
}
